/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { View } from '@az/starc-ui';
import CarouselArrows from './arrows';
import FeatureCard, { type FeatureCardProps } from './FeatureCard';
import styles from './Carousel.module.scss';

export type CarouselCard = FeatureCardProps & { id: string };

export type CarouselProps = {
  cards: CarouselCard[];
};

const Carousel = ({ cards }: CarouselProps) => {
  const carouselRef = useRef<HTMLUListElement>(null);
  const [scrollState, setScrollState] = useState({
    isScrollable: false,
    canScrollPrevious: false,
    canScrollNext: false,
  });

  const updateScrollState = useCallback(() => {
    const carousel = carouselRef.current;
    if (!carousel) {
      return;
    }

    const maxScrollLeft = carousel.scrollWidth - carousel.clientWidth;
    const isScrollable = maxScrollLeft > 1;

    setScrollState({
      isScrollable,
      canScrollPrevious: isScrollable && carousel.scrollLeft > 1,
      canScrollNext: isScrollable && carousel.scrollLeft < maxScrollLeft - 1,
    });
  }, []);

  useEffect(() => {
    updateScrollState();

    const carousel = carouselRef.current;
    const resizeObserver =
      carousel && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(updateScrollState)
        : undefined;

    if (carousel) {
      resizeObserver?.observe(carousel);
    }
    window.addEventListener('resize', updateScrollState);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateScrollState);
    };
  }, [cards.length, updateScrollState]);

  const scroll = (direction: 'previous' | 'next') => {
    const carousel = carouselRef.current;
    if (!carousel) {
      return;
    }

    carousel.scrollBy({
      left: (direction === 'next' ? 1 : -1) * Math.max(carousel.clientWidth * 0.8, 272),
      behavior: 'smooth',
    });
  };

  return (
    <View className={styles.Carousel}>
      {scrollState.isScrollable && (
        <CarouselArrows
          canScrollPrevious={scrollState.canScrollPrevious}
          canScrollNext={scrollState.canScrollNext}
          showOnDesktop={cards.length > 4}
          onPrevious={() => scroll('previous')}
          onNext={() => scroll('next')}
        />
      )}
      <ul ref={carouselRef} className={styles.Row} onScroll={updateScrollState}>
        {cards.map(({ id, ...card }) => (
          <li key={id} className={styles.Item}>
            <FeatureCard {...card} />
          </li>
        ))}
      </ul>
    </View>
  );
};

export default Carousel;
